import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { usePageTitle } from "context/PageTitleContext";
import { Avatar, Tooltip } from "@mui/material";
import { AppsOutlined, KeyboardArrowDown, } from "@mui/icons-material";
import { getFullAvatarUrl, getInitials } from "utils/imageUtils";
import NotificationBell from "../NotificationBell";
import "./AppBar.css";
import "../UserMenu.css";

const APP_RELEASE_VERSION = "26.06.16";

// Map đường dẫn sang tiêu đề trang
const getPageTitle = (pathname) => {
  const pathTitles = {
    "/dashboard/overview": "Dashboard tổng thể",
    "/dashboard/project": "Dashboard hợp đồng",
    "/projects": "Danh sách hợp đồng",
    "/projects/items": "Hạng mục hợp đồng",
    "/projects/history": "Lịch sử hợp đồng",
    "/projects/calendar": "Lịch biểu",
    "/projects/notifications": "Trung tâm thông báo",
    "/notifications": "Trung tâm thông báo",
    "/all-notifications": "Trung tâm thông báo",
    "/customers/info": "Thông tin chủ đầu tư",
    "/customers/projects": "Hợp đồng của chủ đầu tư",
    "/staff/info": "Quản lý nhân sự",
    "/staff/departments": "Phòng ban",
    // Routes cho Quản lý thiết bị
    "/equipment/categories": "Danh mục thiết bị",
    "/equipment/borrow": "Lập phiếu mượn thiết bị",
    "/equipment/return": "Lập phiếu trả thiết bị",
    "/equipment/borrow-history": "Lịch sử mượn thiết bị",
    "/equipment/return-history": "Lịch sử trả thiết bị",
    "/equipment/calibration": "Theo dõi hiệu chuẩn",
    "/accounting/payments": "Quản lý thanh toán",
    "/admin/users": "Quản lý người dùng",
    "/admin/role-permissions": "Quản lý phân quyền theo vai trò",
    "/admin/user-roles": "Quản lý vai trò người dùng",
    "/admin/permissions": "Tổng quan quyền hạn",
    "/admin/categories": "Quản lý danh mục tài liệu",
    "/admin/logs": "Nhật ký hệ thống",
    "/admin/release-log": "Nhật ký phát hành",
    "/profile": "Thông tin cá nhân",
  };

  // Check for dynamic routes
  if (pathname.match(/^\/projects\/[^/]+$/)) {
    return "Chi tiết hợp đồng"; // Default for contract detail pages
  }

  // Debug logging to help identify unmatched routes
  if (!pathTitles[pathname]) {
    
  }

  return pathTitles[pathname] || "Project Trak";
};

const AppBar = ({ onToggleSidebar, variant = "default" }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const avatarRef = useRef(null);
  const { logout, user } = useAuth();
  const { pageTitle, pageSubtitle, setPageTitle, setPageSubtitle } = usePageTitle();
  const navigate = useNavigate();
  const location = useLocation();
  const isModuleLayout = variant === "module" || variant === "subcontract";

  // Chỉ set tiêu đề mặc định cho các route cố định, không override tiêu đề custom
  useEffect(() => {
    // ModuleLayout quản lý riêng tiêu đề hai dòng cho các module route mới.
    if (
      location.pathname.startsWith("/systems") ||
      location.pathname.startsWith("/contracts") ||
      location.pathname.startsWith("/subcontracts") ||
      location.pathname.startsWith("/equipment") ||
      location.pathname.startsWith("/notifications") ||
      location.pathname.startsWith("/profile")
    ) {
      return;
    }

    setPageSubtitle("");

    // Không set tiêu đề cho contract detail pages - để ContractDetail tự set
    if (location.pathname.match(/^\/projects\/[^/]+$/)) {
      return;
    }

    const currentTitle = getPageTitle(location.pathname);
    setPageTitle(currentTitle);
  }, [location.pathname, setPageSubtitle, setPageTitle]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        !avatarRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="app-bar">
      <div className="app-bar-left">
        {onToggleSidebar && (
          <button className="sidebar-toggle" onClick={onToggleSidebar}>
            <span className="material-icons">menu</span>
          </button>
        )}
        <div className="page-title-group">
          <h1 className="page-title">{pageTitle}</h1>
          {pageSubtitle && <p className="page-subtitle">{pageSubtitle}</p>}
        </div>
      </div>
      <div className="app-bar-right">
        {isModuleLayout ? (
          <>
            <NotificationBell variant="inbox" />
            <Tooltip title="Danh sách ứng dụng" arrow>
              <button
                type="button"
                className="subcontract-header-action"
                aria-label="Danh sách ứng dụng"
                onClick={() => navigate("/portal")}
              >
                <AppsOutlined />
              </button>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Quay về trang chủ" arrow>
              <button
                type="button"
                className="app-bar-icon"
                style={{color: '#000000'}}
                aria-label="Quay về trang chủ"
                onClick={() => navigate("/portal")}
              >
                <span className="material-icons">home</span>
              </button>
            </Tooltip>
            <NotificationBell />
          </>
        )}
        <div
          className={`app-bar-avatar ${isModuleLayout ? "subcontract-header-user" : ""}`}
          onClick={() => setShowUserMenu(!showUserMenu)}
          ref={avatarRef}
          style={{ cursor: "pointer" }}
        >
          <Avatar
            src={getFullAvatarUrl(user?.avatarFilePath)}
            alt={user?.fullName || user?.userName}
            sx={{
              width: 32,
              height: 32,
              fontSize: "14px",
              fontFamily: "Inter, sans-serif",
            }}
            slotProps={{
              img: {
                onError: (e) => {
                  // Khi lỗi, chỉ ẩn ảnh và hiển thị chữ cái đầu
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.display = "none";
                },
                // Thêm các thuộc tính để giúp xử lý CORS
                referrerPolicy: "origin",
                crossOrigin: "anonymous",
              },
            }}
          >
            {getInitials(user?.fullName || user?.userName)}
          </Avatar>
          {isModuleLayout && (
            <>
              <span className="subcontract-header-user__name">
                {user?.fullName || user?.userName || "Tài khoản"}
              </span>
              <KeyboardArrowDown className="subcontract-header-user__arrow" />
            </>
          )}
        </div>
        {showUserMenu && (
          <div className="user-menu" ref={userMenuRef}>
            <div className="user-menu-header">
              <Avatar
                src={getFullAvatarUrl(user?.avatarFilePath)}
                alt={user?.fullName || user?.userName}
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: "16px",
                  fontFamily: "Inter, sans-serif",
                  marginRight: "12px",
                }}
                slotProps={{
                  img: {
                    onError: (e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = "none";
                    },
                    referrerPolicy: "origin",
                    crossOrigin: "anonymous",
                  },
                }}
              >
                {getInitials(user?.fullName || user?.userName)}
              </Avatar>
              <div className="greeting">
                Xin chào, {user?.fullName || user?.userName || "Người dùng"}
                <span
                  className={`user-role ${(
                    user?.roleName ||
                    user?.role ||
                    ""
                  ).toLowerCase()}`}
                >
                  {user?.roleName || user?.role || "Vai trò chưa xác định"}
                </span>
              </div>
            </div>
            <div className="user-menu-divider"></div>
            <div
              className="user-menu-item"
              onClick={() => navigate("/profile")}
            >
              <span className="material-icons">person_outline</span>
              Tài khoản
            </div>
            <div className="user-menu-item danger" onClick={handleLogout}>
              <span className="material-icons">logout</span>
              Đăng xuất
            </div>
            <div className="user-menu-release">
              Phiên bản: {APP_RELEASE_VERSION}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppBar;
